import { Focusable } from "@blacknut/spatialnav-sdk/dist";
import clsx from "clsx";
import * as React from "react";
import { useState } from "react";
import { ReactComponent as VisibilityOn } from "../../assets/ic_visibility_on.svg";
import { ReactComponent as VisibilityOff } from "../../assets/ic_visibility_off.svg";
import { ModalSubscription, useModal, usePrevious } from "../../utils";
import VirtualKeyboardModal, {
  VirtualKeyboardModalProps,
} from "../Modals/VirtualKeyboardModal";
import { PinTextField, PinTextFieldSize } from "../PinTextField";
import styles from "./styles.module.scss";
export interface ProfilePinProps extends React.HTMLAttributes<HTMLDivElement> {
  onPinEntered: (s: string) => void;
  errored?: boolean;
  size?: PinTextFieldSize;
  value?: string;
  testID?: string;
  secured?: boolean;
  pinEditing?: boolean;
  setPinEditing?: (b: boolean) => void;
  handlePinChanged?: (s: string) => void;
  useVirtualKeyboard?: boolean;
  virtualKeyboardModalProps?: Pick<
    VirtualKeyboardModalProps,
    "enterType" | "locale" | "parentFocusPath"
  >;
}

const Pin = ({
  value,
  errored = false,
  size = "small",
  className,
  testID,
  onPinEntered,
  secured = true,
  pinEditing = false,
  setPinEditing,
  handlePinChanged,
  useVirtualKeyboard = false,
}: ProfilePinProps) => {
  const [isPinHidden, setIsPinHidden] = useState<boolean>(true);

  let char0AtInit = "";
  let char1AtInit = "";
  let char2AtInit = "";
  let char3AtInit = "";
  if (value && value.length >= 4) {
    char0AtInit = value.charAt(0);
    char1AtInit = value.charAt(1);
    char2AtInit = value.charAt(2);
    char3AtInit = value.charAt(3);
  }

  const [char0, setChar0] = React.useState(char0AtInit);
  const [char1, setChar1] = React.useState(char1AtInit);
  const [char2, setChar2] = React.useState(char2AtInit);
  const [char3, setChar3] = React.useState(char3AtInit);
  const emptyValueOnFocus = React.useRef(true);
  const ref0 = React.useRef<HTMLInputElement | null>(null);
  const ref1 = React.useRef<HTMLInputElement | null>(null);
  const ref2 = React.useRef<HTMLInputElement | null>(null);
  const ref3 = React.useRef<HTMLInputElement | null>(null);

  const { push } = useModal();
  const previousValue = usePrevious(value);
  React.useEffect(() => {
    if (value && previousValue != value) {
      let _char0 = char0;
      let _char1 = char1;
      let _char2 = char2;
      let _char3 = char3;
      const len = value.length;
      if (len <= 1) _char0 = value.charAt(0);
      if (len <= 2) _char1 = value.charAt(1);
      if (len <= 3) _char2 = value.charAt(2);
      if (len <= 4) _char3 = value.charAt(3);
      if (_char0 !== char0) setChar0(_char0);
      if (_char1 !== char1) setChar1(_char1);
      if (_char2 !== char2) setChar2(_char2);
      if (_char3 !== char3) setChar3(_char3);

      setChar0(value.charAt(0));
      setChar1(value.charAt(1));
      setChar2(value.charAt(2));
      setChar3(value.charAt(3));
    }
  }, [char0, char1, char2, char3, value, previousValue]);

  const onChange = React.useCallback(
    (ch1: string, ch2: string, ch3: string, ch4: string) => {
      if (!pinEditing && setPinEditing) setPinEditing(true);
      const newVal = `${ch1}${ch2}${ch3}${ch4}`;
      if (ch1 && ch2 && ch3 && ch4) {
        onPinEntered(newVal);
        emptyValueOnFocus.current = true;
      } else if (handlePinChanged) {
        handlePinChanged(newVal);
      }
    },
    [handlePinChanged, onPinEntered, pinEditing, setPinEditing]
  );

  const previousErrored = usePrevious(errored);
  React.useEffect(() => {
    if (previousErrored != undefined && !previousErrored && errored) {
      setChar0("");
      setChar1("");
      setChar2("");
      setChar3("");
    }
  }, [errored, previousErrored]);

  const onChangePin0 = React.useCallback(
    (s: string | null, inputHasFocus: boolean) => {
      const ch0 = s && s.length > 0 ? s.charAt(0) : "";
      setChar0(ch0);
      if (!inputHasFocus && emptyValueOnFocus.current) {
        setChar1("");
        setChar2("");
        setChar3("");
        onChange(ch0, "", "", "");
        emptyValueOnFocus.current = false;
      } else {
        onChange(ch0, char1, char2, char3);
      }

      if (s) {
        if (inputHasFocus) {
          setTimeout(() => {
            ref1.current && ref1.current.focus();
          }, 0);
        } else {
          //Give focus to the surrounding container
          const focusable = ref1.current && ref1.current.closest("[tabIndex]");
          if (focusable) {
            (focusable as HTMLElement).focus();
          }
        }
      }
    },
    [char1, char2, char3, onChange]
  );

  const onChangePin1 = React.useCallback(
    (s: string | null, inputHasFocus: boolean) => {
      const ch1 = s && s.length > 0 ? s.charAt(0) : "";

      setChar1(ch1);

      if (!inputHasFocus && emptyValueOnFocus.current) {
        setChar0("");
        setChar2("");
        setChar3("");
        onChange("", ch1, "", "");
        emptyValueOnFocus.current = false;
      } else {
        onChange(char0, ch1, char2, char3);
      }

      if (s) {
        if (inputHasFocus) {
          setTimeout(() => {
            ref2.current && ref2.current.focus();
          }, 0);
        } else {
          //Give focus to the surrounding container
          const focusable = ref2.current && ref2.current.closest("[tabIndex]");
          if (focusable) {
            (focusable as HTMLElement).focus();
          }
        }
      }
    },
    [char0, char2, char3, onChange]
  );

  const onChangePin2 = React.useCallback(
    (s: string | null, inputHasFocus: boolean) => {
      const ch2 = s && s.length > 0 ? s.charAt(0) : "";
      setChar2(ch2);

      if (!inputHasFocus && emptyValueOnFocus.current) {
        setChar0("");
        setChar1("");
        setChar3("");
        onChange("", "", ch2, "");
        emptyValueOnFocus.current = false;
      } else {
        onChange(char0, char1, ch2, char3);
      }

      if (s) {
        if (inputHasFocus) {
          setTimeout(() => {
            ref3.current && ref3.current.focus();
          }, 0);
        } else {
          //Give focus to the surrounding container
          const focusable = ref3.current && ref3.current.closest("[tabIndex]");
          if (focusable) {
            (focusable as HTMLElement).focus();
          }
        }
      }
    },
    [char0, char1, char3, onChange]
  );
  const onChangePin3 = React.useCallback(
    (s: string | null, inputHasFocus: boolean) => {
      const ch3 = s && s.length > 0 ? s.charAt(0) : "";
      setChar3(ch3);

      if (!inputHasFocus && emptyValueOnFocus.current) {
        setChar0("");
        setChar1("");
        setChar2("");
        onChange("", "", "", ch3);
        emptyValueOnFocus.current = false;
      } else {
        onChange(char0, char1, char2, ch3);
      }
      if (s) {
        ref3.current && ref3.current.blur();
      }
    },
    [char0, char1, char2, onChange]
  );

  const onFocusInput = React.useCallback(() => {
    // clears input the first time
    if (emptyValueOnFocus.current) {
      setChar0("");
      setChar1("");
      setChar2("");
      setChar3("");
      onChange("", "", "", "");
      emptyValueOnFocus.current = false;
    }
  }, [onChange]);

  const togglePinHidden = React.useCallback(() => {
    setIsPinHidden((b) => !b);
  }, []);

  const [vkActive, setVKActive] = useState(-1);

  const modalSubscription = React.useRef<ModalSubscription>();

  const onShowVirtualKeyboard = React.useCallback(
    (i: number) => {
      let _char0 = char0;
      let _char1 = char1;
      let _char2 = char2;
      let _char3 = char3;
      if (i === 0) _char0 = "";
      if (i <= 1) _char1 = "";
      if (i <= 2) _char2 = "";
      if (i <= 3) _char3 = "";
      if (_char0 !== char0) setChar0(_char0);
      if (_char1 !== char1) setChar1(_char1);
      if (_char2 !== char2) setChar2(_char2);
      if (_char3 !== char3) setChar3(_char3);
      setVKActive(i);
      onChange(_char0, _char1, _char2, _char3);
      modalSubscription.current = push((props) => {
        const _setValue = (s: string) => {
          const len = s.length;
          if (len <= 1) _char0 = s.charAt(0);
          if (len <= 2) _char1 = s.charAt(1);
          if (len <= 3) _char2 = s.charAt(2);
          if (len <= 4) _char3 = s.charAt(3);
          if (_char0 !== char0) setChar0(_char0);
          if (_char1 !== char1) setChar1(_char1);
          if (_char2 !== char2) setChar2(_char2);
          if (_char3 !== char3) setChar3(_char3);
          onChange(_char0, _char1, _char2, _char3);
          if (s.length < 4) {
            setVKActive(s.length);
          } else {
            setVKActive(-1);
            modalSubscription.current?.remove();
            modalSubscription.current = undefined;
          }
        };
        const onClose = () => {
          setVKActive(-1);
          modalSubscription.current?.remove();
          modalSubscription.current = undefined;
        };
        return (
          <VirtualKeyboardModal
            {...props}
            setValue={_setValue}
            onClose={onClose}
            type="numeric"
          />
        );
      });
    },
    [char0, char1, char2, char3, onChange, push]
  );
  const onShowVirtualKeyboard0 = React.useCallback(() => {
    onShowVirtualKeyboard(0);
  }, [onShowVirtualKeyboard]);
  const onShowVirtualKeyboard1 = React.useCallback(() => {
    onShowVirtualKeyboard(1);
  }, [onShowVirtualKeyboard]);
  const onShowVirtualKeyboard2 = React.useCallback(() => {
    onShowVirtualKeyboard(2);
  }, [onShowVirtualKeyboard]);
  const onShowVirtualKeyboard3 = React.useCallback(() => {
    onShowVirtualKeyboard(3);
  }, [onShowVirtualKeyboard]);

  return (
    <div className={clsx(styles.container, className)} data-testid={testID}>
      <PinTextField
        testID="code0"
        value={char0}
        size={size}
        errored={errored}
        onChangePin={onChangePin0}
        inputRef={ref0}
        onInputFocus={onFocusInput}
        secured={pinEditing ? isPinHidden : secured}
        useVirtualKeyboard={useVirtualKeyboard}
        onShowVirtualKeyboard={onShowVirtualKeyboard0}
        active={vkActive === 0}
      />

      <PinTextField
        testID="code1"
        value={char1}
        size={size}
        errored={errored}
        onChangePin={onChangePin1}
        inputRef={ref1}
        onInputFocus={onFocusInput}
        secured={pinEditing ? isPinHidden : secured}
        useVirtualKeyboard={useVirtualKeyboard}
        onShowVirtualKeyboard={onShowVirtualKeyboard1}
        active={vkActive === 1}
      />

      <PinTextField
        testID="code2"
        value={char2}
        size={size}
        errored={errored}
        onChangePin={onChangePin2}
        inputRef={ref2}
        onInputFocus={onFocusInput}
        secured={pinEditing ? isPinHidden : secured}
        useVirtualKeyboard={useVirtualKeyboard}
        onShowVirtualKeyboard={onShowVirtualKeyboard2}
        active={vkActive === 2}
      />

      <PinTextField
        testID="code3"
        value={char3}
        size={size}
        errored={errored}
        onChangePin={onChangePin3}
        inputRef={ref3}
        onInputFocus={onFocusInput}
        secured={pinEditing ? isPinHidden : secured}
        useVirtualKeyboard={useVirtualKeyboard}
        onShowVirtualKeyboard={onShowVirtualKeyboard3}
        active={vkActive === 3}
      />

      {pinEditing && (
        <Focusable
          className={styles.eye}
          onPressEnter={togglePinHidden}
          onClick={togglePinHidden}
        >
          {isPinHidden ? <VisibilityOff /> : <VisibilityOn />}
        </Focusable>
      )}
    </div>
  );
};
export default Pin;
export { Pin };
